import { Dialog, DialogContent, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MarketButton, MarketHeader, MarketDivider } from '@market/react';
import { useOrderDetails } from 'components/context/order_details';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useTranslation } from 'react-i18next';
import { VALIDATION_STATUSES } from 'components/pages/checkout/hooks/use_address_validation';
import { ReactComponent as XIcon } from 'images/icons/x-dismiss-icon.svg';
import { trackCdp } from 'utilities/tracking';
import { SHIPPING_INFORMATION_VALIDATION } from 'constants/cdp_events';

import styles from 'components/pages/checkout/components/shipping_address_selection_modal.module.scss';

const ShippingAddressSelectionModal = ({
  open,
  handleContinue,
  handleDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { number: orderNumber } = useOrderDetails();
  const { addressValidationResponse, addressValidationStatus } =
    useCheckoutFlow();

  const showUseSuggestedStatuses = new Set([
    VALIDATION_STATUSES.PARTIALLY_VERIFIED,
    VALIDATION_STATUSES.VERIFIED,
  ]);

  if (!addressValidationResponse) {
    return null;
  }

  const {
    original_address: originalAddress,
    corrected_address: correctedAddress,
    address_service_statuses: {
      status: addressResultStatus,
      verification_status: verificationStatus,
    },
  } = addressValidationResponse;

  const trackingParams = {
    shop_validation_status: addressValidationStatus,
    service_result_status: addressResultStatus,
    service_verification_status: verificationStatus,
    order_id: orderNumber,
  };

  const renderPrimaryButtonCTAText = () => {
    if (showUseSuggestedStatuses.has(addressValidationStatus)) {
      return t('checkout.information.addressValidation.useSuggested');
    }

    return t('checkout.information.addressValidation.enterNew');
  };

  const handleSelectSuggested = () => {
    trackCdp({
      name: SHIPPING_INFORMATION_VALIDATION.CTA.USE_SUGGESTED,
      params: trackingParams,
    });

    handleContinue({
      address_line_1: correctedAddress.address_line_1,
      address_line_2: correctedAddress.address_line_2,
      locality: correctedAddress.locality,
      administrative_district_level_1:
        correctedAddress.administrative_district_level_1,
      postal_code: correctedAddress.postal_code,
    });
  };

  const handleUseOriginal = () => {
    trackCdp({
      name: SHIPPING_INFORMATION_VALIDATION.CTA.USE_ORIGINAL,
      params: trackingParams,
    });

    handleContinue({
      address_line_1: originalAddress.address_line_1,
      address_line_2: originalAddress.address_line_2,
      locality: originalAddress.locality,
      administrative_district_level_1:
        originalAddress.administrative_district_level_1,
      postal_code: originalAddress.postal_code,
    });
  };

  const handleEnterNew = () => {
    trackCdp({
      name: SHIPPING_INFORMATION_VALIDATION.CTA.ENTER_NEW,
      params: trackingParams,
    });

    handleDismiss();
  };

  const handleClose = () => {
    trackCdp({
      name: SHIPPING_INFORMATION_VALIDATION.CTA.CLOSE_MODAL,
      params: trackingParams,
    });

    handleDismiss();
  };

  return (
    <Dialog
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      open={open}
      PaperProps={{
        style: { borderRadius: '12px', padding: '4px 10px' },
      }}
    >
      <DialogContent>
        <button
          data-testid="close-button"
          className={styles.customDismissButton}
          onClick={handleClose}
        >
          <XIcon />
        </button>
        <MarketHeader>
          <p className={`${styles.modalHeader} heading-30`}>
            {t('checkout.information.addressValidation.header')}
          </p>
          <p className="paragraph-30">
            {t('checkout.information.addressValidation.subheader')}
          </p>
        </MarketHeader>
        <MarketDivider margin="medium" className={styles.divider} />
        <div
          className={
            fullScreen
              ? styles.addressSelectionContainerMobile
              : styles.addressSelectionContainer
          }
          style={{
            gap:
              fullScreen &&
              addressValidationStatus !== VALIDATION_STATUSES.PARTIALLY_VERIFIED
                ? '0px'
                : '48px',
          }}
        >
          <div className={styles.addressContainer}>
            <div className={styles.addressContainerHeader}>
              {t('checkout.information.addressValidation.youEntered')}
            </div>
            <div className={styles.addressText}>
              <p>{originalAddress.address_line_1}</p>
              <p>{originalAddress.address_line_2}</p>
              <p>{`${originalAddress.locality}, ${originalAddress.administrative_district_level_1}`}</p>
              <p>{originalAddress.postal_code}</p>
            </div>
            <MarketButton
              className={styles.ctaButton}
              onClick={handleUseOriginal}
            >
              {t('checkout.information.addressValidation.useOriginal')}
            </MarketButton>
          </div>
          <div
            className={styles.addressContainer}
            style={{
              justifyContent:
                addressValidationStatus ===
                  VALIDATION_STATUSES.PARTIALLY_VERIFIED && 'flex-end',
            }}
          >
            {showUseSuggestedStatuses.has(addressValidationStatus) && (
              <>
                <div className={styles.addressContainerHeader}>
                  {t('checkout.information.addressValidation.suggested')}
                </div>
                <div className={styles.addressText}>
                  <p>{correctedAddress?.address_line_1}</p>
                  <p>{correctedAddress?.address_line_2}</p>
                  <p>{`${correctedAddress?.locality}, ${correctedAddress?.administrative_district_level_1}`}</p>
                  <p>{correctedAddress?.postal_code}</p>
                </div>
              </>
            )}
            <div className={styles.secondaryButtonContainer}>
              <MarketButton
                className={styles.ctaButton}
                onClick={
                  showUseSuggestedStatuses.has(addressValidationStatus)
                    ? handleSelectSuggested
                    : handleEnterNew
                }
                rank="primary"
              >
                {renderPrimaryButtonCTAText()}
              </MarketButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShippingAddressSelectionModal;
